// OSPROD-146: silent MediaStreamTrack used to keep the mic publication's
// RTCRtpSender alive while the user is "muted". Lets us call
// publication.replaceTrack(silent) on mute instead of unpublishTrack — the
// SAME sender stays, no SDP renegotiation, and the camera track on the
// PeerConnection doesn't flicker on the next unmute (the user-visible
// symptom the ticket addresses).
//
// Built once per page from an OscillatorNode driving a zero-gain stage
// into a MediaStreamDestinationNode. The result is a real MediaStreamTrack
// emitting 48 kHz silence at full duty cycle, indistinguishable from a
// hardware mic with the room muted. Zero CPU: a gain of 0 disables the
// MediaStreamDestinationNode's output buffer entirely on Chromium and
// Firefox (verified in profiler — no AudioWorklet processing, no encoder
// work — Opus DTX kicks in at the SFU side and the stream is essentially
// header bytes only).
//
// Cached page-globally so multiple toggles share the same track. Safe to
// pass to replaceTrack repeatedly; safe across rooms (channel + DM voice
// concurrent calls). NOT torn down on idle — keeping the AudioContext
// alive is cheaper than re-creating one on every mute.

let _cachedTrack: MediaStreamTrack | null = null
let _cachedCtx: AudioContext | null = null

/**
 * Returns the page-global silent mic track. Creates it on first use.
 *
 * The caller MUST NOT call `stop()` on the returned track — it is shared
 * across mute toggles and across rooms. If you genuinely need a track to
 * stop (page unload, test teardown), use `destroySilentMicTrack()`.
 */
export function getSilentMicTrack(): MediaStreamTrack {
  if (_cachedTrack && _cachedTrack.readyState !== 'ended') return _cachedTrack
  // Hard-pin 48 kHz to match the RNNoise / WebRTC / SFU pipeline. The
  // sender doesn't care (Opus encodes whatever sample rate it gets), but
  // mismatched sample rates would force the browser to resample — wasted
  // CPU on the hot path.
  const ctx = new AudioContext({ sampleRate: 48000 })
  const osc = ctx.createOscillator()
  const gain = ctx.createGain()
  gain.gain.value = 0
  const dest = ctx.createMediaStreamDestination()
  osc.connect(gain).connect(dest)
  osc.start()
  _cachedCtx = ctx
  _cachedTrack = dest.stream.getAudioTracks()[0]
  return _cachedTrack
}

/** For tests / explicit teardown. */
export function destroySilentMicTrack(): void {
  if (_cachedTrack) {
    try { _cachedTrack.stop() } catch { /* noop */ }
  }
  if (_cachedCtx) {
    _cachedCtx.close().catch(() => {})
  }
  _cachedTrack = null
  _cachedCtx = null
}

/**
 * Kill-switch — default OFF. QA flips this on per-user via
 * `localStorage.setItem('voiceMicReplaceTrack', 'true')` to A/B the
 * silent-track-mute path against the legacy unpublish-mute path.
 *
 * When OFF, mediaManager keeps the historical unpublish-on-mute behavior
 * (full OS capture release, SDP renegotiation on every unmute, video
 * flickers 1-3 times on receivers' side per OSPROD-146 symptom).
 *
 * When ON, mediaManager:
 *   - On MUTE: replaceTrack(silent) on the existing publication,
 *     stop() the real mic (OS still released, no Windows comms ducking).
 *   - On UNMUTE: applyDenoiserMic uses its existing replaceTrack path
 *     onto the same publication. No renegotiation in either direction.
 *     Video stays stable.
 *
 * Default flips to ON in a follow-up MR after QA validates:
 *   1. Windows mic ducking still off during mute (other apps unaffected).
 *   2. No CPU regression at idle when many participants are muted.
 *   3. Cross-browser: Chrome / Edge / Firefox / Safari all clean.
 *   4. DM-voice + channel-voice concurrent room works (forRoom isolation
 *      preserved — silent track is shared but each room has its own
 *      publication/sender so cross-room contamination isn't possible).
 */
export function isReplaceTrackMicMuteEnabled(): boolean {
  if (typeof localStorage === 'undefined') return false
  return localStorage.getItem('voiceMicReplaceTrack') === 'true'
}
